'use client'

import { useState, useEffect, useCallback } from 'react'
import { useNavigate } from 'react-router-dom'
import { PropertyWithLandlord } from '@/types'
import { useCurrency } from '@/lib/currency'
import EmptyState from '@/components/EmptyState'
import { PropertyListSkeleton } from '@/components/SkeletonLoader'
import ConfirmDialog from '@/components/ConfirmDialog'
import { getApiUrl } from '@/lib/apiUrl'

interface SavedPropertiesProps {
  userId: string
}

export default function SavedProperties({ userId }: SavedPropertiesProps) {
  const { format } = useCurrency()
  const navigate = useNavigate()
  const [savedProperties, setSavedProperties] = useState<PropertyWithLandlord[]>([])
  const [loading, setLoading] = useState(true)
  const [removingId, setRemovingId] = useState<string | null>(null)
  const [confirmRemoveId, setConfirmRemoveId] = useState<string | null>(null)
  // Load user's saved properties
  const loadSavedProperties = useCallback(async () => {
    setLoading(true)
    try {
      const token = localStorage.getItem('authToken')
      if (!token) {
        console.error('No auth token found')
        return
      }

      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/users/saved-properties`, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
      })
      
      if (!response.ok) {
        console.error('Error loading saved properties:', response.statusText)
        return
      }

      const data = await response.json()

      // Normalize backend format -> frontend format (id + images URLs + location string)
      const props = (data.savedProperties || []).map((p: any) => ({
        ...p,
        id: p._id || p.id,
        images: (p.images || []).map((img: any) => (typeof img === 'string' ? img : img.url)).filter(Boolean),
        location:
          p.location ||
          (p.address && typeof p.address === 'object'
            ? [p.address.street, p.address.city, p.address.state].filter(Boolean).join(', ')
            : 'Location not specified')
      }))

      setSavedProperties(props)
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setLoading(false)
    }
  }, [userId])

  useEffect(() => {
    loadSavedProperties()
  }, [loadSavedProperties])

  // Remove property from saved list
  const handleRemoveSaved = async (propertyId: string) => {
    setRemovingId(propertyId)
    try {
      const token = localStorage.getItem('authToken')
      const apiUrl = getApiUrl()
      const response = await fetch(`${apiUrl}/users/saved-properties`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          propertyId,
          action: 'unsave'
        })
      })

      if (!response.ok) {
        console.error('Error removing saved property:', response.statusText)
      } else {
        // Remove from local state
        setSavedProperties(prev => prev.filter(p => String(p.id) !== String(propertyId)))
      }
    } catch (error) {
      console.error('Error:', error)
    } finally {
      setRemovingId(null)
      setConfirmRemoveId(null)
    }
  }


  // Format price via currency context
  const formatPrice = (price: number) => format(price)

  // Format duration
  const formatDuration = (months: number) => {
    if (months === 12) return '1 year'
    if (months === 6) return '6 months'
    if (months === 18) return '18 months'
    if (months === 24) return '2 years'
    return `${months} months`
  }

  // Get landlord display name
  const getLandlordDisplayName = (landlord: any) => {
    if (!landlord) return 'Unknown Landlord'
    if (landlord.kyc_data?.firstName && landlord.kyc_data?.lastName) {
      return `${landlord.kyc_data.firstName} ${landlord.kyc_data.lastName}`
    }
    return landlord.email || 'Verified Landlord'
  }

  if (loading) {
    return <PropertyListSkeleton count={3} />
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h2 className="text-xl font-semibold text-gray-900">Saved Properties</h2>
          <p className="text-sm text-gray-600">
            Your favorite properties for quick access
          </p>
        </div>
        <button
          onClick={loadSavedProperties}
          className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 transition-colors text-sm"
        >
          Refresh
        </button>
      </div>

      {/* Stats */}
      <div className="bg-white rounded-lg shadow p-4">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-lg font-medium text-gray-900">{savedProperties.length} Saved Properties</h3>
            <p className="text-sm text-gray-500">
              {savedProperties.length > 0 
                ? `Average price: ${formatPrice(
                    savedProperties.reduce((sum, sp) => sum + (sp.price || 0), 0) / savedProperties.length
                  )}`
                : 'No saved properties yet'
              }
            </p>
          </div>
          {/* {savedProperties.length > 0 && (
            <button className="text-red-600 hover:text-red-700 text-sm">
              Clear All
            </button>
          )} */}
        </div>
      </div>

      {/* Saved Properties Grid */}
      {savedProperties.length > 0 ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {savedProperties.map((property) => (
            <div key={property.id} className="bg-white rounded-lg shadow-md overflow-hidden hover:shadow-lg transition-shadow">
              {/* Property Image */}
              <div className="h-48 bg-gray-200 relative overflow-hidden property-image-container">
                {property.images && property.images.length > 0 ? (
                  <img
                    src={property.images[0]}
                    alt={property.title}
                    className="w-full h-full object-cover transition-transform hover:scale-105"
                    onError={(e) => {
                      e.currentTarget.src = '/placeholder-property.svg'
                    }}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-gray-400">
                    <svg className="w-12 h-12" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" />
                    </svg>
                  </div>
                )}
                <div className="absolute top-2 right-2 bg-green-500 text-white text-xs px-2 py-1 rounded">
                  ✅ Saved
                </div>
                <div className="absolute top-2 left-2 bg-blue-500 text-white text-xs px-2 py-1 rounded">
                  {(property as any).is_verified ? '✓ Verified' : 'Pending'}
                </div>
              </div>

              {/* Property Details */}
              <div className="p-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-2">
                  {property.title}
                </h3>
                <p className="text-gray-600 text-sm mb-3">{(property as any).location}</p>
                <p className="text-gray-700 text-sm mb-4 line-clamp-2">
                  {property.description}
                </p>
                
                <div className="flex justify-between items-center mb-4">
                  <div>
                    <p className="text-2xl font-bold text-green-600">
                      {formatPrice(property.price || 0)}
                    </p>
                    <p className="text-sm text-gray-500">per month</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-medium text-gray-900">
                      {formatDuration((property as any).duration || (property as any).leaseTerms?.minLease || 12)}
                    </p>
                    <p className="text-xs text-gray-500">lease term</p>
                  </div>
                </div>

                <div className="border-t pt-4">
                  <div className="flex justify-between items-center text-sm text-gray-500 mb-3">
                    <span>Landlord: {getLandlordDisplayName((property as any).landlord)}</span>
                    <span>{property.images?.length || 0} photos</span>
                  </div>
                  
                  <div className="flex space-x-2">
                    <button
                      onClick={() => navigate(`/property/${property.id}`)}
                      className="flex-1 bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 transition-colors text-sm"
                    >
                      View Details
                    </button>
                    <button
                      onClick={() => setConfirmRemoveId(String(property.id))}
                      disabled={removingId === String(property.id)}
                      className="flex-1 bg-red-600 text-white py-2 px-4 rounded-md hover:bg-red-700 disabled:opacity-50 transition-colors text-sm"
                    >
                      {removingId === String(property.id) ? 'Removing...' : 'Remove'}
                    </button>
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <EmptyState
          icon={
            <svg className="w-16 h-16 text-stone-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M4.318 6.318a4.5 4.5 0 000 6.364L12 20.364l7.682-7.682a4.5 4.5 0 00-6.364-6.364L12 7.636l-1.318-1.318a4.5 4.5 0 00-6.364 0z" />
            </svg>
          }
          title="No Saved Properties"
          description="Start saving properties you love! Browse available properties and click the heart icon to save them for later."
          action={{
            label: 'Browse Properties',
            href: '/dashboard/client'
          }}
        />
      )}

      {/* Export Saved Properties (Future Enhancement) */}
      {/* {savedProperties.length > 0 && (
        // <div className="bg-white rounded-lg shadow p-4">
        //   <h3 className="text-sm font-medium text-gray-700 mb-2">Quick Actions</h3>
        //   <div className="flex space-x-3">
        //     <button className="text-blue-600 hover:text-blue-700 text-sm">
        //       📧 Email List to Myself
        //     </button>
        //     <button className="text-blue-600 hover:text-blue-700 text-sm">
        //       📱 Share List
        //     </button>
        //     <button className="text-blue-600 hover:text-blue-700 text-sm">
        //       📊 Compare Properties
        //     </button>
        //   </div>
        // </div>
      )} */}

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmRemoveId !== null}
        title="Remove Saved Property"
        message="Are you sure you want to remove this property from your saved list? You can always save it again later."
        confirmText="Remove"
        cancelText="Cancel"
        variant="warning"
        onConfirm={() => confirmRemoveId && handleRemoveSaved(confirmRemoveId)}
        onCancel={() => setConfirmRemoveId(null)}
      />
    </div>
  )
} 

